import { SceneTypeDto } from "@repo/api/overlays";
import IconLetterCaseToggle from "../Icons/IconLetterCaseToggle";
import IconPhoto from "../Icons/IconPhoto";
import IconRectangle from "../Icons/IconRectangle";
import IconTemplates from "../Icons/IconTemplates";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import OverlayTemplateDialog from "../OverlayTemplate/OverlayTemplateDialog";
import { forwardRef } from "react";
import { getResolution } from "@repo/api/utils/composition";

type AddElementBottomBarProps = {
  getInputProps: () => any;
  open: () => void;
  sceneType: SceneTypeDto;
};

export default function AddElementBottomBar(props: AddElementBottomBarProps) {
  const aspectRatio = useEditorStore(
    (state) => state.video?.compositionJson.aspectRatio,
  );
  const addElementToComposition = useEditorStore(
    (state) => state.service.addElementToComposition,
  );
  const getNewTextTypography = useEditorStore(
    (state) => state.service.getNewTextTypography,
  );
  const setIsMediaSelectorDialogOpen = useEditorStore(
    (state) => state.service.setIsMediaSelectorDialogOpen,
  );
  const updateOverlaysTabState = useEditorStore(
    (state) => state.service.updateOverlaysTabState,
  );

  const startColor = "var(--color-primary-400)";
  const endColor = "var(--color-secondary-400)";

  if (!aspectRatio) {
    return null;
  }

  const resolution = getResolution(aspectRatio);

  return (
    <div className="mx-2 mb-2 flex items-center justify-evenly rounded-xl border border-border py-1.5">
      <AddElementItem
        icon={
          <>
            <IconPhoto startColor={startColor} endColor={endColor} />
            <input {...props.getInputProps()} className="h-full w-full" />
          </>
        }
        label="Media"
        onClick={() => {
          setIsMediaSelectorDialogOpen(true);
        }}
      />
      <AddElementItem
        icon={
          <IconLetterCaseToggle startColor={startColor} endColor={endColor} />
        }
        label="Text"
        onClick={() => {
          const elementExternalId = crypto.randomUUID();
          const width = 205; // Checked manually for Montserrat with "Text" and fontSize 100
          const height = 150; // Checked manually for Montserrat with "Text" and fontSize 100
          const x = Math.round((resolution.width - width) / 2);
          const y = Math.round((resolution.height - height) / 2);

          addElementToComposition(
            {
              externalId: elementExternalId,
              type: "TEXT",
              text: "Text",
              position: {
                type: "ABSOLUTE",
                x,
                y,
              },
              opacity: 100,
              color: {
                type: "SOLID_COLOR",
                format: "HEX",
                color: "#FFFFFF",
              },
              typography: getNewTextTypography(),
              shadow: null,
              enterAt: {
                timing: null,
                animation: null,
              },
              exitAt: {
                timing: null,
                animation: null,
              },
            },
            aspectRatio,
            props.sceneType,
          );

          updateOverlaysTabState((state) => {
            state.currentElement = {
              externalId: elementExternalId,
              sceneType: props.sceneType,
            };
          });
        }}
      />
      <AddElementItem
        icon={<IconRectangle startColor={startColor} endColor={endColor} />}
        label="Rectangle"
        onClick={() => {
          const elementExternalId = crypto.randomUUID();
          const rectangleSize = 250;
          const x = Math.round((resolution.width - rectangleSize) / 2);
          const y = Math.round((resolution.height - rectangleSize) / 2);

          addElementToComposition(
            {
              externalId: elementExternalId,
              type: "RECTANGLE",
              position: {
                type: "ABSOLUTE",
                x,
                y,
              },
              size: {
                type: "ABSOLUTE",
                w: rectangleSize,
                h: rectangleSize,
              },
              backgroundColor: {
                type: "SOLID_COLOR",
                format: "HEX",
                color: "#D9D9D9",
              },
              opacity: 100,
              cornerRadius: 0,
              border: null,
              shadow: null,
              enterAt: {
                timing: null,
                animation: null,
              },
              exitAt: {
                timing: null,
                animation: null,
              },
            },
            aspectRatio,
            props.sceneType,
          );

          updateOverlaysTabState((state) => {
            state.currentElement = {
              externalId: elementExternalId,
              sceneType: props.sceneType,
            };
          });
        }}
      />

      <OverlayTemplateDialog>
        <AddElementItem
          icon={<IconTemplates startColor={startColor} endColor={endColor} />}
          label="Templates"
          onClick={() => {}}
        />
      </OverlayTemplateDialog>
    </div>
  );
}

type AddElementItemProps = {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
};

const AddElementItem = forwardRef<HTMLButtonElement, AddElementItemProps>(
  ({ icon, label, onClick }, ref) => {
    return (
      <button
        ref={ref}
        className="flex h-15 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-md transition-colors hover:bg-grey-100 [&_svg]:size-6"
        onClick={onClick}
      >
        {icon}
        <span className="text-sm font-semibold">{label}</span>
      </button>
    );
  },
);
